import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wrench, PhoneCall, PackageCheck, Truck, ArrowRight } from "lucide-react";
import api from "../api";
import Seo from "../components/Seo";
import { Helmet } from "react-helmet-async";
import { useLang, Link } from "../i18n";
import { DISMANTLING } from "../data/dismantling";
import Reveal from "../components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const BE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith("/") ? `${BE}${u}` : u);

const HOW = [
  { icon: Wrench, t: "dz.h1t", d: "dz.h1d" },
  { icon: PackageCheck, t: "dz.h2t", d: "dz.h2d" },
  { icon: Truck, t: "dz.h3t", d: "dz.h3d" },
];

export default function Dismantling() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(DISMANTLING);
  const [selected, setSelected] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/parts")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) {
          setItems(
            r.data.map((p) => ({
              ...p,
              fuel: { en: p.fuel_en, ro: p.fuel_ro },
              desc: { en: p.desc_en, ro: p.desc_ro },
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const partsLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("dz.title"),
    numberOfItems: items.length,
    itemListElement: items.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.title,
        ...(p.images?.[0]?.startsWith("https://") ? { image: p.images[0] } : {}),
      },
    })),
  };

  const open = (item) => {
    setSelected(item);
    setActive(0);
  };

  return (
    <div data-testid="dismantling-page" className="pt-32 pb-24">
            <Seo title={t("seo.dzTitle")} description={t("seo.dzDesc")} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(partsLd)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">KATACO</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50">{t("dz.title")}</h1>
          <p className="mt-5 text-slate-400 max-w-2xl">{t("dz.sub")}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="dismantling-how">
          {HOW.map((h, i) => {
            const Icon = h.icon;
            return (
              <Reveal key={h.t} delay={i * 0.08}>
                <div className="h-full bg-[#12141D] border border-white/10 rounded-2xl p-7">
                  <Icon size={24} className="text-[#4499F7]" />
                  <h3 className="mt-5 font-display font-semibold text-slate-50">{t(h.t)}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t(h.d)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="dismantling-grid">
          {items.map((item, i) => (
            <Reveal key={item.id} delay={(i % 4) * 0.07}>
              <button
                data-testid={`dismantling-card-${item.id}`}
                onClick={() => open(item)}
                className="group w-full text-left bg-[#12141D] border border-white/10 rounded-2xl overflow-hidden hover:border-[rgba(68,153,247,0.4)] transition-colors duration-500"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={imgSrc(item.images[0])}
                    alt={item.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10]/90 via-transparent to-transparent" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-semibold leading-snug text-slate-100 group-hover:text-[#4499F7] transition-colors">
                    {item.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400 font-mono-num tracking-wide">
                    {item.year && <span>{item.year}</span>}
                    {item.engine && <span>{item.engine}</span>}
                    {item.km != null && <span>{item.km.toLocaleString("en-US").replace(/,/g, " ")} km</span>}
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div data-testid="dismantling-note" className="mt-16 rounded-3xl border border-[rgba(68,153,247,0.25)] bg-[#12141D] p-8 lg:p-10 text-center spotlight-card overflow-hidden">
            <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">{t("dz.note")}</p>
            <Link
              to="/contact"
              data-testid="dismantling-note-link"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4499F7] hover:text-[#6FB1FA] transition-colors"
            >
              {t("dz.noteLink")}
              <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#12141D] border-white/10 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dismantling-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-slate-50">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <motion.img
                  key={active}
                  src={imgSrc(selected.images[active])}
                  alt={selected.title}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>
              {selected.images.length > 1 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {selected.images.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`dismantling-thumb-${i}`}
                      onClick={() => setActive(i)}
                      className={`relative aspect-[16/10] rounded-lg overflow-hidden border transition-colors ${
                        i === active ? "border-[#4499F7]" : "border-white/10 hover:border-white/30"
                      }`}
                    >
                      <img src={imgSrc(img)} alt="" loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400 font-mono-num tracking-wide">
                {selected.year && <span>{selected.year}</span>}
                {selected.engine && <span>{selected.engine}</span>}
                {selected.fuel?.en && <span>{selected.fuel[lang]}</span>}
                {selected.km != null && <span>{selected.km.toLocaleString("en-US").replace(/,/g, " ")} km</span>}
              </div>
              <p className="mt-4 text-slate-300 leading-relaxed" data-testid="dismantling-dialog-desc">
                {selected.desc?.[lang] || selected.desc?.en || ""}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <Link
                  to={`/contact?part=${encodeURIComponent(selected.title)}`}
                  data-testid="dismantling-enquire-btn"
                  className="group flex items-center gap-2 bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-6 py-3 hover:bg-[#6FB1FA] transition-colors"
                >
                  {t("dz.enquire")}
                  <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <a
                  href="tel:+40723250260"
                  data-testid="dismantling-call-btn"
                  className="flex items-center gap-2 border border-white/20 text-slate-100 font-semibold text-sm rounded-full px-6 py-3 hover:border-[rgba(68,153,247,0.6)] hover:text-[#4499F7] transition-colors"
                >
                  <PhoneCall size={15} />
                  +40 723 250 260
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
